import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import React, { useEffect, useMemo } from "react";
import { createPath, useNavigate } from "react-router-dom";
import {
  MyProject,
  MyProjectSpreadsheetState,
  MyTodo,
  MyTodoSpreadsheetState,
  usePageState,
} from ".";
import { useAppState } from "../../app";

import { useGlobalNotifier, useSpreadsheetState } from "@vrf/fr-vrf-core-react";
import { FrVrfPanel } from "@vrf/fr-vrf-web-components-react";
import { useGetHomeMenuDataList } from "../../services/home";
import { ApplicationPage } from "../application-page";
import { ENV } from "@vrf/fr-vrf-core";
import {
  MemoEditDialogContent,
  MemoType,
  useMemoEditDialogState,
} from "../dialog/memo-edit-dialog";
import { DeploymentRequestScreenProcessingDivisionType } from "../../util/general-code/enums/general-codes";

const TEMPLATE_FILE_URL = {
  MY_PROJECT_LIST: "spreadsheet-template/my-project-list-page.xlsx",
  MY_TODO_LIST: "spreadsheet-template/my-todo-list-page.xlsx",
};

export const HomePageContent = () => {
  const { showConfirmationDialog, showToast, resetUserError } =
    useGlobalNotifier();
  const navigate = useNavigate();

  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [getHomeMenuDataListRequest, sendGetHomeMenuDataListRequest] =
    useGetHomeMenuDataList();

  const [
    myProjectSpreadsheetState,
    setMyProjectSpreadsheetState,
    initMyProjectSpreadsheetState,
  ] = useSpreadsheetState<MyProjectSpreadsheetState>();
  const myProjectSpreadsheetInitialData =
    useMemo<MyProjectSpreadsheetState>(() => {
      return {
        myProjectList: pageState.searchResult.myProjectList,
      };
    }, [pageState.searchResult.myProjectList]);

  const [
    myTodoSpreadsheetState,
    setMyTodoSpreadsheetState,
    initMyTodoSpreadsheetState,
  ] = useSpreadsheetState<MyTodoSpreadsheetState>();
  const myTodoSpreadsheetInitialData = useMemo<MyTodoSpreadsheetState>(() => {
    return {
      myTodoList: pageState.searchResult.myTodoList,
    };
  }, [pageState.searchResult.myTodoList]);

  const [memoEditDialogState, setMemoEditDialogState] =
    useMemoEditDialogState();

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: "JEDI | HOME",
      pjtId: "",
      cpntPjtId: "",
    }));
    sendGetHomeMenuDataListRequest({
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
    });

    console.log("-- Init display Start --");
    console.log(memoEditDialogState);
    console.log("-- Init display End --");
  };

  const onGetHomeMenuDataListRequestResponded = () => {
    if (!getHomeMenuDataListRequest) return;

    const myProjectList: MyProject[] =
      getHomeMenuDataListRequest.myProjectList ?? [];
    const myTodoList: MyTodo[] =
      getHomeMenuDataListRequest.myTodoList.map((e) => {
        // For displaying detail column of MyTodoList.
        let mode = "";
        switch (e.transitionId) {
          case ApplicationPage.INTERFACE_LIST:
            mode = e.transitionPrm?.mode
              ? "[" + e.transitionPrm?.mode + "]"
              : "";
            break;
          case ApplicationPage.INTERFACE_DEPLOYMENT_REQUEST_DETAIL:
          case ApplicationPage.COMPONENT_DEPLOYMENT_REQUEST_LIST:
            switch (e.transitionPrm?.screenMode) {
              case DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE:
                mode = "[review]";
                break;
              case DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE:
                mode = "[withdraw]";
                break;
              case DeploymentRequestScreenProcessingDivisionType.RETRY_MODE:
                mode = "[retry]";
                break;
              default:
                mode = "";
            }
            break;
          default:
            mode = "";
            break;
        }
        return {
          ...e,
          transitionPageName:
            ApplicationPage.pageById(e.transitionId).label + mode,
        };
      }) ?? [];
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        myProjectList: myProjectList,
        myTodoList: myTodoList,
      },
    }));
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetHomeMenuDataListRequestResponded, [
    getHomeMenuDataListRequest,
  ]);

  const onCellClicked = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const selectedRow = event.detail.value;

    if (eventName === "myTodoMemoEditClicked" && selectedRow) {
      console.log("---- myTodoMemoEditClicked Edit Start ------");
      console.log(selectedRow);

      setMemoEditDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        pjtId: selectedRow.pjtId,
        tcktTypeCd: selectedRow.tcktTypeCd,
        tcktStsTypeCd: selectedRow.tcktStsTypeCd,
        mmTxt: selectedRow.mmTxt,
        updateDateTime: selectedRow.mmTxtUpdateDateTime,
        targetIndex: selectedRow.no,
        memoType: MemoType.TICKET_STATUS,
      }));

      console.log("---- myTodoMemoEditClicked Edit End ------");
      return;
    }

    if (eventName === "myProjectMemoEditClicked" && selectedRow) {
      console.log("---- myProjectMemoEditClicked Edit Start ------");
      console.log(selectedRow);

      setMemoEditDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        pjtId: selectedRow.pjtId,
        tcktTypeCd: "",
        tcktStsTypeCd: "",
        mmTxt: selectedRow.mmTxt,
        updateDateTime: selectedRow.mmTxtUpdateDateTime,
        targetIndex: selectedRow.no,
        memoType: MemoType.PROJECT,
      }));

      console.log("---- myProjectMemoEditClicked Edit End ------");
      return;
    }

    if (
      (eventName === "pjtNameClicked" && selectedRow) ||
      (eventName === "detailClicked" && selectedRow)
    ) {
      let params: { [key: string]: string } = selectedRow.transitionPrm;

      const queryString = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");

      const href = createPath({
        pathname: ApplicationPage.pathById(selectedRow.transitionId),
        search: queryString,
      });

      navigate(href);
      return;
    }
    if (
      (eventName === "pjtName_newTabIcon" && selectedRow) ||
      (eventName === "detail_newTabIcon" && selectedRow)
    ) {
      let params: { [key: string]: string } = selectedRow.transitionPrm;

      const queryString = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");

      const href = createPath({
        pathname: ApplicationPage.pathById(selectedRow.transitionId),
        search: queryString,
      });
      window.open(window.location.pathname + "#" + href);
      return;
    }
    if (eventName === "todoPjtNameClicked" && selectedRow) {
      let params: { [key: string]: string } = selectedRow.transitionPrjPrm;

      const queryString = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");

      const href = createPath({
        pathname: ApplicationPage.pathById(selectedRow.transitionPrjId),
        search: queryString,
      });
      navigate(href);
      return;
    }
    if (eventName === "todoPjtName_newTabIcon" && selectedRow) {
      let params: { [key: string]: string } = selectedRow.transitionPrjPrm;

      const queryString = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");

      const href = createPath({
        pathname: ApplicationPage.pathById(selectedRow.transitionPrjId),
        search: queryString,
      });
      window.open(window.location.pathname + "#" + href);
      return;
    }
  };

  /** Set edited memo to spreadsheet state */
  const onCloseMemoEditDialog = () => {
    if (memoEditDialogState.isShow) return;
    if (!myProjectSpreadsheetState) return;
    if (!myTodoSpreadsheetState) return;
    const targetIndex = memoEditDialogState.targetIndex;
    const memoType = memoEditDialogState.memoType;

    console.log("==onCloseMemoEditDialog==");
    console.log(memoEditDialogState);

    if (memoType === MemoType.PROJECT) {
      const newMyProjectList: MyProject[] =
        myProjectSpreadsheetState.myProjectList.map((e) => {
          if (e.no === targetIndex) {
            return {
              ...e,
              mmTxt: memoEditDialogState.mmTxt,
              mmTxtUpdateDateTime: memoEditDialogState.updateDateTime,
            };
          }
          return e;
        });
      setMyProjectSpreadsheetState({
        ...myProjectSpreadsheetState,
        myProjectList: newMyProjectList,
      });
    }

    if (memoType === MemoType.TICKET_STATUS) {
      const newMyTodoList: MyTodo[] = myTodoSpreadsheetState.myTodoList.map(
        (e) => {
          if (e.no === targetIndex) {
            return {
              ...e,
              mmTxt: memoEditDialogState.mmTxt,
              mmTxtUpdateDateTime: memoEditDialogState.updateDateTime,
            };
          }
          return e;
        },
      );
      setMyTodoSpreadsheetState({
        ...myTodoSpreadsheetState,
        myTodoList: newMyTodoList,
      });
    }
  };
  useEffect(onCloseMemoEditDialog, [memoEditDialogState.isShow]);

  return (
    <>
      {memoEditDialogState.isShow && <MemoEditDialogContent />}

      <div>
        <FrVrfPanel panelTitle="My Project List">
          <FrVrfSpreadsheet
            data={myProjectSpreadsheetInitialData}
            templateUrl={TEMPLATE_FILE_URL.MY_PROJECT_LIST}
            onInitialized={initMyProjectSpreadsheetState}
            onCellClicked={onCellClicked}
            height={330}
          />
        </FrVrfPanel>
        <div style={{ marginBottom: "1%" }} />
        <FrVrfPanel panelTitle="My Todo List">
          <FrVrfSpreadsheet
            data={myTodoSpreadsheetInitialData}
            templateUrl={TEMPLATE_FILE_URL.MY_TODO_LIST}
            onInitialized={initMyTodoSpreadsheetState}
            onCellClicked={onCellClicked}
            height={330}
          />
        </FrVrfPanel>
      </div>
    </>
  );
};
